import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import { authService } from './auth.service'
import type { ApiResponse, CourseCategory, SkillLevel } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        // Construct base URL using configurable API version from Constants.ts
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

interface Course {
    id: string
    title: string
    description: string
    full_price: number
    discount_price?: number
    category: CourseCategory
    is_active: boolean
    hidden: boolean
    duration_hours?: number
    skill_level?: SkillLevel
    thumbnail_url?: string
    created_at: string
    updated_at: string
}

interface Bundle {
    id: string
    title: string
    description: string
    full_price: number
    discount_price?: number
    course_ids: string[]
    is_active: boolean
    hidden: boolean
    thumbnail_url?: string
    savings_percentage?: number
    created_at: string
    updated_at: string
}

interface CourseFormData {
    title: string
    description: string
    full_price: string
    discount_price: string
    category: CourseCategory
    is_active: boolean
    hidden: boolean
    duration_hours: string
    skill_level: SkillLevel
    thumbnail_url: string
}

interface BundleFormData {
    title: string
    description: string
    full_price: string
    discount_price: string
    course_ids: string[]
    is_active: boolean
    hidden: boolean
    thumbnail_url: string
    savings_percentage: string
}

class AdminProductsService {
    /**
     * Validate authentication token exists
     */
    private validateAuth(): void {
        if (!authService.isAuthenticated()) {
            throw new Error('AUTHENTICATION_REQUIRED: No authentication token found. Please log in.')
        }
    }

    /**
     * Handle authentication failures by logging out user
     */
    private handleAuthenticationFailure(error: any): void {
        const errorMessage = error.message || String(error)
        
        if (
            errorMessage.includes('Could not validate credentials') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('Invalid token') ||
            errorMessage.includes('Token expired') ||
            errorMessage.includes('AUTHENTICATION_REQUIRED') ||
            error.response?.status === 401
        ) {
            console.error('Authentication failure detected. Logging out user...')
            
            setTimeout(() => {
                authService.logout()
            }, 1000)
            
            throw new Error('Your session has expired. Please log in again.')
        }
    }

    // ============================================================================
    // Course Operations
    // ============================================================================

    /**
     * Fetch all courses with pagination
     */
    async fetchCourses(page: number = 1, pageSize: number = 100): Promise<ApiResponse<Course[]>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get('/courses', {
                params: { page, page_size: pageSize }
            })
            
            const courses = response.data?.data?.courses || []
            
            return {
                success: true,
                data: courses
            }
        } catch (error: any) {
            console.error('Error fetching courses:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch courses'
            }
        }
    }

    /**
     * Create a new course
     */
    async createCourse(courseForm: CourseFormData): Promise<ApiResponse<Course>> {
        try {
            this.validateAuth()
            
            const payload = {
                title: courseForm.title,
                description: courseForm.description,
                full_price: parseFloat(courseForm.full_price),
                discount_price: courseForm.discount_price ? parseFloat(courseForm.discount_price) : undefined,
                category: courseForm.category,
                is_active: courseForm.is_active,
                hidden: courseForm.hidden,
                duration_hours: courseForm.duration_hours ? parseInt(courseForm.duration_hours) : undefined,
                skill_level: courseForm.skill_level,
                thumbnail_url: courseForm.thumbnail_url || undefined
            }

            const response = await getApiInstance().post('/courses', payload)
            const course = response.data?.data || response.data
            
            return {
                success: true,
                data: course
            }
        } catch (error: any) {
            console.error('Error creating course:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to create course'
            }
        }
    }

    /**
     * Update an existing course
     */
    async updateCourse(courseId: string, courseForm: CourseFormData): Promise<ApiResponse<Course>> {
        try {
            this.validateAuth()
            
            const payload = {
                title: courseForm.title,
                description: courseForm.description,
                full_price: parseFloat(courseForm.full_price),
                discount_price: courseForm.discount_price ? parseFloat(courseForm.discount_price) : undefined,
                category: courseForm.category,
                is_active: courseForm.is_active,
                hidden: courseForm.hidden,
                duration_hours: courseForm.duration_hours ? parseInt(courseForm.duration_hours) : undefined,
                skill_level: courseForm.skill_level,
                thumbnail_url: courseForm.thumbnail_url || undefined
            }

            const response = await getApiInstance().put(`/courses/${courseId}`, payload)
            const course = response.data?.data || response.data
            
            return {
                success: true,
                data: course
            }
        } catch (error: any) {
            console.error('Error updating course:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update course'
            }
        }
    }

    /**
     * Delete a course
     */
    async deleteCourse(courseId: string): Promise<ApiResponse<void>> {
        try {
            this.validateAuth()
            await getApiInstance().delete(`/courses/${courseId}`)
            
            return {
                success: true,
                data: undefined
            }
        } catch (error: any) {
            console.error('Error deleting course:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to delete course'
            }
        }
    }

    // ============================================================================
    // Bundle Operations
    // ============================================================================

    /**
     * Fetch all bundles with pagination
     */
    async fetchBundles(page: number = 1, pageSize: number = 100): Promise<ApiResponse<Bundle[]>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get('/bundles', {
                params: { page, page_size: pageSize }
            })
            
            const bundles = response.data?.data?.bundles || []
            
            return {
                success: true,
                data: bundles
            }
        } catch (error: any) {
            console.error('Error fetching bundles:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch bundles'
            }
        }
    }

    /**
     * Create a new bundle
     */
    async createBundle(bundleForm: BundleFormData): Promise<ApiResponse<Bundle>> {
        try {
            this.validateAuth()
            
            const payload = {
                title: bundleForm.title,
                description: bundleForm.description,
                full_price: parseFloat(bundleForm.full_price),
                discount_price: bundleForm.discount_price ? parseFloat(bundleForm.discount_price) : undefined,
                course_ids: bundleForm.course_ids,
                is_active: bundleForm.is_active,
                hidden: bundleForm.hidden,
                thumbnail_url: bundleForm.thumbnail_url || undefined,
                savings_percentage: bundleForm.savings_percentage ? parseFloat(bundleForm.savings_percentage) : undefined
            }

            const response = await getApiInstance().post('/bundles', payload)
            const bundle = response.data?.data || response.data
            
            return {
                success: true,
                data: bundle
            }
        } catch (error: any) {
            console.error('Error creating bundle:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to create bundle'
            }
        }
    }

    /**
     * Update an existing bundle
     */
    async updateBundle(bundleId: string, bundleForm: BundleFormData): Promise<ApiResponse<Bundle>> {
        try {
            this.validateAuth()
            
            const payload = {
                title: bundleForm.title,
                description: bundleForm.description,
                full_price: parseFloat(bundleForm.full_price),
                discount_price: bundleForm.discount_price ? parseFloat(bundleForm.discount_price) : undefined,
                course_ids: bundleForm.course_ids,
                is_active: bundleForm.is_active,
                hidden: bundleForm.hidden,
                thumbnail_url: bundleForm.thumbnail_url || undefined,
                savings_percentage: bundleForm.savings_percentage ? parseFloat(bundleForm.savings_percentage) : undefined
            }

            const response = await getApiInstance().put(`/bundles/${bundleId}`, payload)
            const bundle = response.data?.data || response.data
            
            return {
                success: true,
                data: bundle
            }
        } catch (error: any) {
            console.error('Error updating bundle:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update bundle'
            }
        }
    }

    /**
     * Delete a bundle
     */
    async deleteBundle(bundleId: string): Promise<ApiResponse<void>> {
        try {
            this.validateAuth()
            await getApiInstance().delete(`/bundles/${bundleId}`)
            
            return {
                success: true,
                data: undefined
            }
        } catch (error: any) {
            console.error('Error deleting bundle:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to delete bundle'
            }
        }
    }

    // ============================================================================
    // Bulk Visibility Operations
    // ============================================================================

    /**
     * Bulk update course visibility
     */
    async bulkUpdateCourseVisibility(courseIds: string[], hidden: boolean): Promise<ApiResponse<any>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().patch('/courses/bulk-visibility', {
                course_ids: courseIds,
                hidden
            })
            
            return {
                success: true,
                data: response.data?.data
            }
        } catch (error: any) {
            console.error('Error updating course visibility:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update course visibility'
            }
        }
    }

    /**
     * Bulk update bundle visibility
     */
    async bulkUpdateBundleVisibility(bundleIds: string[], hidden: boolean): Promise<ApiResponse<any>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().patch('/bundles/bulk-visibility', {
                bundle_ids: bundleIds,
                hidden
            })
            
            return {
                success: true,
                data: response.data?.data
            }
        } catch (error: any) {
            console.error('Error updating bundle visibility:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update bundle visibility'
            }
        }
    }
}

// Export singleton instance
export const adminProductsService = new AdminProductsService()
export default adminProductsService

// Re-export types for convenience
export type { CourseCategory, SkillLevel } from '../types/api.types'
